import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Card, Col, Form, Modal, ProgressBar, Row } from 'react-bootstrap';
import Spinner from 'react-bootstrap/Spinner';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import HeaderNavbar from '../components/HeaderNavbar';
import Footer from '../components/Footer';
import PersonalConnections from '../components/settings/PersonalConnections';
import config from '../config';
import api from '../lib/request';
import { useAuthContext } from '../contexts/AuthContext';
import { evaluatePasswordStrength } from '../lib/passwordRules';
import './Settings.css';

const SETTINGS_SECTIONS = [
  { id: 'account', label: 'Account' },
  { id: 'security', label: 'Security' },
  { id: 'connections', label: 'Connections' }
];

const DEFAULT_SETTINGS_STATE = {
  connections: {
    openAi: {
      connected: false,
      apiKeyPreview: null
    }
  }
};

const DEFAULT_PROFILE_STATE = {
  username: '',
  email: '',
  firstName: '',
  lastName: '',
  organizations: [],
  isSiteAdmin: false
};

const TWO_FACTOR_METHODS = Object.freeze({
  DISABLED: 'disabled',
  EMAIL: 'email',
  AUTHENTICATOR: 'authenticator'
});

const DEFAULT_TWO_FACTOR_STATE = {
  enabled: false,
  method: TWO_FACTOR_METHODS.DISABLED,
  recoveryCodesRemaining: 0,
  configuredAt: null,
  recoveryCodesGeneratedAt: null,
  updatedAt: null
};

const DEFAULT_SECURITY_STATE = {
  twoFactor: DEFAULT_TWO_FACTOR_STATE
};

const EMPTY_TWO_FACTOR_FLOW = {
  show: false,
  purpose: 'change',
  desiredMethod: TWO_FACTOR_METHODS.DISABLED,
  challengeToken: '',
  verificationMethod: TWO_FACTOR_METHODS.EMAIL,
  allowRecoveryCode: false,
  useRecoveryCode: false,
  password: '',
  code: '',
  recoveryCode: '',
  destinationPreview: '',
  qrCodeDataUrl: '',
  manualEntryKey: '',
  expiresAt: null,
  expiresInMs: 0,
  loading: false,
  error: ''
};

function normalizeTwoFactorState(value) {
  const raw = value && typeof value === 'object' ? value : {};
  const method = Object.values(TWO_FACTOR_METHODS).includes(raw.method)
    ? raw.method
    : TWO_FACTOR_METHODS.DISABLED;

  if (raw.enabled !== true || method === TWO_FACTOR_METHODS.DISABLED) {
    return { ...DEFAULT_TWO_FACTOR_STATE };
  }

  return {
    enabled: true,
    method,
    recoveryCodesRemaining: Number.isFinite(Number(raw.recoveryCodesRemaining))
      ? Math.max(0, Number(raw.recoveryCodesRemaining))
      : 0,
    configuredAt: raw.configuredAt || null,
    recoveryCodesGeneratedAt: raw.recoveryCodesGeneratedAt || null,
    updatedAt: raw.updatedAt || null
  };
}

function normalizeSecurityState(value) {
  const raw = value && typeof value === 'object' ? value : {};
  return {
    twoFactor: normalizeTwoFactorState(raw.twoFactor)
  };
}

function getTwoFactorMethodLabel(method) {
  switch (method) {
    case TWO_FACTOR_METHODS.EMAIL:
      return 'Email';
    case TWO_FACTOR_METHODS.AUTHENTICATOR:
      return 'Authenticator app';
    case TWO_FACTOR_METHODS.DISABLED:
    default:
      return 'Disabled';
  }
}

function buildRecoveryCodesText(codes) {
  return (Array.isArray(codes) ? codes : []).join('\n');
}

function normalizeSection(value) {
  const normalized = (value || '').toString().trim().toLowerCase();
  return SETTINGS_SECTIONS.some((section) => section.id === normalized) ? normalized : 'account';
}

function buildSectionSearch(sectionId) {
  return sectionId === 'account' ? '' : `?section=${encodeURIComponent(sectionId)}`;
}


function Settings() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, isAuthReady, login } = useAuthContext();
  const isMountedRef = useRef(false);

  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState('');
  const [settingsUnavailable, setSettingsUnavailable] = useState(false);
  const [profile, setProfile] = useState(DEFAULT_PROFILE_STATE);
  const [security, setSecurity] = useState(DEFAULT_SECURITY_STATE);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS_STATE);

  const [accountForm, setAccountForm] = useState({ firstName: '', lastName: '' });
  const [accountSubmitting, setAccountSubmitting] = useState(false);
  const [accountError, setAccountError] = useState('');
  const [accountSuccess, setAccountSuccess] = useState('');

  const [securityForm, setSecurityForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [securitySubmitting, setSecuritySubmitting] = useState(false);
  const [securityError, setSecurityError] = useState('');
  const [securitySuccess, setSecuritySuccess] = useState('');

  const [twoFactorMethodSelection, setTwoFactorMethodSelection] = useState(TWO_FACTOR_METHODS.DISABLED);
  const [twoFactorError, setTwoFactorError] = useState('');
  const [twoFactorSuccess, setTwoFactorSuccess] = useState('');
  const [twoFactorFlow, setTwoFactorFlow] = useState(EMPTY_TWO_FACTOR_FLOW);
  const [recoveryCodesModal, setRecoveryCodesModal] = useState({
    show: false,
    codes: [],
    title: '',
    message: '',
    copyState: 'idle'
  });


  const activeSection = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return normalizeSection(params.get('section'));
  }, [location.search]);

  const passwordStrength = useMemo(
    () => evaluatePasswordStrength(securityForm.newPassword),
    [securityForm.newPassword]
  );

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const loadSettings = useCallback(async () => {
    if (isMountedRef.current) {
      setLoading(true);
      setPageError('');
    }
    try {
      const response = await api.get('/api/v1/settings');
      const nextProfile = response?.profile || DEFAULT_PROFILE_STATE;
      const nextSecurity = normalizeSecurityState(response?.security);
      const nextSettings = response?.settings || DEFAULT_SETTINGS_STATE;

      if (!isMountedRef.current) {
        return;
      }

      setProfile({
        ...DEFAULT_PROFILE_STATE,
        ...nextProfile,
        organizations: Array.isArray(nextProfile?.organizations) ? nextProfile.organizations : []
      });
      setSecurity(nextSecurity);
      setSettings({
        ...DEFAULT_SETTINGS_STATE,
        ...nextSettings,
        connections: {
          ...DEFAULT_SETTINGS_STATE.connections,
          ...(nextSettings?.connections || {})
        }
      });
      setSettingsUnavailable(response?.settingsUnavailable === true);
      setAccountForm({
        firstName: nextProfile?.firstName || '',
        lastName: nextProfile?.lastName || ''
      });
      setTwoFactorMethodSelection(nextSecurity.twoFactor.method);
    } catch (error) {
      if (!isMountedRef.current) {
        return;
      }
      setPageError(error?.message || 'Unable to load your settings. Please try again later.');
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    if (!isAuthReady) {
      return;
    }
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }
    loadSettings();
  }, [isAuthReady, isAuthenticated, loadSettings]);

  const redirectTarget = `${location.pathname}${location.search}`;
  if (!isAuthReady) {
    return (
      <>
        <HeaderNavbar />
        <div className="settings-page">
          <div className="settings-loading-state">
            <Spinner animation="border" />
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to={`${config.prefixPath}/login?goback=${encodeURIComponent(redirectTarget)}&rl=1`} replace />;
  }

  async function refreshSessionToken(nextToken) {
    if (!nextToken) {
      return true;
    }
    const ok = await login(nextToken);
    if (ok) {
      return true;
    }
    window.location.href = `${config.prefixPath}/login?goback=${encodeURIComponent(redirectTarget)}&rl=1`;
    return false;
  }

  function handleSectionChange(sectionId) {
    navigate({
      pathname: `${config.prefixPath}/settings`,
      search: buildSectionSearch(sectionId)
    });
  }

  function handleAccountFieldChange(field) {
    return (event) => {
      setAccountForm((prev) => ({
        ...prev,
        [field]: event.target.value
      }));
    };
  }

  async function handleAccountSubmit(event) {
    event.preventDefault();
    setAccountError('');
    setAccountSuccess('');

    if (!accountForm.firstName.trim()) {
      setAccountError('First name is required.');
      return;
    }
    if (!accountForm.lastName.trim()) {
      setAccountError('Last name is required.');
      return;
    }

    setAccountSubmitting(true);
    try {
      const response = await api.put('/api/v1/settings/account', {
        firstName: accountForm.firstName,
        lastName: accountForm.lastName
      });

      const refreshed = await refreshSessionToken(response?.auth_token);
      if (!refreshed) return;

      const nextProfile = response?.profile || {};
      setProfile((prev) => ({
        ...prev,
        ...nextProfile,
        organizations: Array.isArray(nextProfile?.organizations) ? nextProfile.organizations : prev.organizations
      }));
      setAccountForm({
        firstName: nextProfile?.firstName || accountForm.firstName.trim(),
        lastName: nextProfile?.lastName || accountForm.lastName.trim()
      });
      setAccountSuccess('Your account details were updated.');
    } catch (error) {
      const reasons = Array.isArray(error?.reasons) && error.reasons.length
        ? ` ${error.reasons.join(' ')}`
        : '';
      setAccountError(error?.message ? `${error.message}${reasons}` : 'Unable to update your account.');
    } finally {
      setAccountSubmitting(false);
    }
  }

  function handleSecurityFieldChange(field) {
    return (event) => {
      setSecurityForm((prev) => ({
        ...prev,
        [field]: event.target.value
      }));
    };
  }

  async function handlePasswordSubmit(event) {
    event.preventDefault();
    setSecurityError('');
    setSecuritySuccess('');

    if (!securityForm.currentPassword) {
      setSecurityError('Current password is required.');
      return;
    }
    if (!securityForm.newPassword) {
      setSecurityError('New password is required.');
      return;
    }
    if (!passwordStrength.isStrong) {
      setSecurityError('Password must meet all strength requirements.');
      return;
    }
    if (securityForm.newPassword !== securityForm.confirmPassword) {
      setSecurityError('Passwords do not match.');
      return;
    }
    if (securityForm.currentPassword === securityForm.newPassword) {
      setSecurityError('Choose a different password.');
      return;
    }

    setSecuritySubmitting(true);
    try {
      const response = await api.post('/api/v1/settings/security/password', {
        currentPassword: securityForm.currentPassword,
        newPassword: securityForm.newPassword,
        confirmPassword: securityForm.confirmPassword
      });

      const refreshed = await refreshSessionToken(response?.auth_token);
      if (!refreshed) return;

      setSecurityForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setSecuritySuccess(response?.message || 'Your password was updated.');
    } catch (error) {
      const reasons = Array.isArray(error?.reasons) && error.reasons.length
        ? ` ${error.reasons.join(' ')}`
        : '';
      setSecurityError(error?.message ? `${error.message}${reasons}` : 'Unable to update your password.');
    } finally {
      setSecuritySubmitting(false);
    }
  }

  function closeTwoFactorFlow() {
    setTwoFactorFlow(EMPTY_TWO_FACTOR_FLOW);
  }

  function openTwoFactorChangeFlow() {
    setTwoFactorError('');
    setTwoFactorSuccess('');
    setTwoFactorFlow({
      ...EMPTY_TWO_FACTOR_FLOW,
      show: true,
      purpose: 'change',
      desiredMethod: twoFactorMethodSelection
    });
  }

  function openRecoveryCodeRegenerationFlow() {
    setTwoFactorError('');
    setTwoFactorSuccess('');
    setTwoFactorFlow({
      ...EMPTY_TWO_FACTOR_FLOW,
      show: true,
      purpose: 'recovery-codes',
      desiredMethod: security.twoFactor.method
    });
  }

  function applyTwoFactorChallengeResponse(response, purpose, desiredMethod) {
    setTwoFactorFlow({
      ...EMPTY_TWO_FACTOR_FLOW,
      show: true,
      purpose,
      desiredMethod,
      challengeToken: response?.challengeToken || '',
      verificationMethod: response?.verificationMethod || TWO_FACTOR_METHODS.EMAIL,
      allowRecoveryCode: response?.allowRecoveryCode === true,
      destinationPreview: response?.destinationPreview || '',
      qrCodeDataUrl: response?.qrCodeDataUrl || '',
      manualEntryKey: response?.manualEntryKey || '',
      expiresAt: response?.expiresAt || null,
      expiresInMs: Number(response?.expiresInMs) || 0,
      loading: false,
      error: ''
    });
  }

  async function handleTwoFactorFlowSubmit(event) {
    event.preventDefault();
    setTwoFactorFlow((prev) => ({
      ...prev,
      loading: true,
      error: ''
    }));

    try {
      if (!twoFactorFlow.challengeToken) {
        if (!twoFactorFlow.password) {
          setTwoFactorFlow((prev) => ({
            ...prev,
            loading: false,
            error: 'Password is required.'
          }));
          return;
        }

        const startPath = twoFactorFlow.purpose === 'change'
          ? '/api/v1/settings/security/two-factor/change/start'
          : '/api/v1/settings/security/two-factor/recovery-codes/regenerate/start';
        const requestBody = twoFactorFlow.purpose === 'change'
          ? { method: twoFactorFlow.desiredMethod, password: twoFactorFlow.password }
          : { password: twoFactorFlow.password };

        const response = await api.post(startPath, requestBody);
        if (response?.requiresVerification) {
          applyTwoFactorChallengeResponse(response, twoFactorFlow.purpose, twoFactorFlow.desiredMethod);
          return;
        }

        throw new Error(response?.message || 'Unable to start the two-factor authentication flow.');
      }

      if (twoFactorFlow.useRecoveryCode) {
        if (!twoFactorFlow.recoveryCode.trim()) {
          setTwoFactorFlow((prev) => ({
            ...prev,
            loading: false,
            error: 'Backup recovery code is required.'
          }));
          return;
        }
      } else if (!twoFactorFlow.code.trim()) {
        setTwoFactorFlow((prev) => ({
          ...prev,
          loading: false,
          error: twoFactorFlow.verificationMethod === TWO_FACTOR_METHODS.AUTHENTICATOR
            ? 'Authenticator app code is required.'
            : 'Verification code is required.'
        }));
        return;
      }

      const verifyPath = twoFactorFlow.purpose === 'change'
        ? '/api/v1/settings/security/two-factor/change/verify'
        : '/api/v1/settings/security/two-factor/recovery-codes/regenerate/verify';
      const response = await api.post(verifyPath, {
        challengeToken: twoFactorFlow.challengeToken,
        code: twoFactorFlow.useRecoveryCode ? '' : twoFactorFlow.code,
        recoveryCode: twoFactorFlow.useRecoveryCode ? twoFactorFlow.recoveryCode : ''
      });

      if (response?.requiresVerification) {
        applyTwoFactorChallengeResponse(response, twoFactorFlow.purpose, twoFactorFlow.desiredMethod);
        return;
      }

      if (response?.completed) {
        const nextSecurity = response?.security
          ? normalizeSecurityState(response.security)
          : security;

        const refreshed = await refreshSessionToken(response?.auth_token);
        if (!refreshed) return;

        setSecurity(nextSecurity);
        setTwoFactorMethodSelection(nextSecurity.twoFactor.method);
        closeTwoFactorFlow();
        setTwoFactorError('');
        setTwoFactorSuccess(response?.message || 'Two-factor authentication settings were updated.');

        if (Array.isArray(response?.recoveryCodes) && response.recoveryCodes.length) {
          setRecoveryCodesModal({
            show: true,
            codes: response.recoveryCodes,
            title: twoFactorFlow.purpose === 'change'
              ? 'Save your backup recovery codes'
              : 'New backup recovery codes',
            message: response?.message || 'Store these backup recovery codes in a safe place. Each code can be used once.',
            copyState: 'idle'
          });
        }

        return;
      }

      throw new Error(response?.message || 'Unable to verify the two-factor authentication step.');
    } catch (error) {
      setTwoFactorFlow((prev) => ({
        ...prev,
        loading: false,
        error: error?.message || 'Unable to continue the two-factor authentication flow.'
      }));
    }
  }

  async function handleTwoFactorChallengeResend() {
    if (!twoFactorFlow.challengeToken) {
      return;
    }

    setTwoFactorFlow((prev) => ({
      ...prev,
      loading: true,
      error: ''
    }));

    try {
      const response = await api.post('/api/v1/settings/security/two-factor/challenge/resend', {
        challengeToken: twoFactorFlow.challengeToken
      });

      if (response?.requiresVerification) {
        applyTwoFactorChallengeResponse(response, twoFactorFlow.purpose, twoFactorFlow.desiredMethod);
        return;
      }

      throw new Error(response?.message || 'Unable to resend the verification email.');
    } catch (error) {
      setTwoFactorFlow((prev) => ({
        ...prev,
        loading: false,
        error: error?.message || 'Unable to resend the verification email.'
      }));
    }
  }

  async function handleCopyRecoveryCodes() {
    try {
      await navigator.clipboard.writeText(buildRecoveryCodesText(recoveryCodesModal.codes));
      setRecoveryCodesModal((prev) => ({
        ...prev,
        copyState: 'copied'
      }));
    } catch (error) {
      setRecoveryCodesModal((prev) => ({
        ...prev,
        copyState: 'failed'
      }));
    }
  }

  function handleDownloadRecoveryCodes() {
    const text = buildRecoveryCodesText(recoveryCodesModal.codes);
    if (!text) {
      return;
    }

    const blob = new Blob([
      `StepWiseDocs backup recovery codes\n\n${text}\n`
    ], { type: 'text/plain;charset=utf-8' });
    const downloadUrl = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = downloadUrl;
    anchor.download = 'stepwisedocs-recovery-codes.txt';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    window.URL.revokeObjectURL(downloadUrl);
  }

  function renderAccountSection() {
    return (
      <Card className="settings-module-card">
        <Card.Body>
          <div className="settings-module-header">
            <h1>Account</h1>
            <p>Manage the core information attached to your StepWiseDocs account.</p>
          </div>

          <Card className="settings-subsection-card">
            <Card.Body>
              <div className="settings-subsection-header">
                <h2>Profile</h2>
                <p>Update the profile details used across your StepWiseDocs account.</p>
              </div>

              {accountError ? <Alert variant="danger">{accountError}</Alert> : null}
              {accountSuccess ? <Alert variant="success">{accountSuccess}</Alert> : null}

              <Form onSubmit={handleAccountSubmit} noValidate>
                <Row className="g-3">
                  <Col md={6}>
                    <Form.Group controlId="settingsFirstName">
                      <Form.Label>First name</Form.Label>
                      <Form.Control
                        type="text"
                        value={accountForm.firstName}
                        onChange={handleAccountFieldChange('firstName')}
                        autoComplete="given-name"
                        maxLength={120}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group controlId="settingsLastName">
                      <Form.Label>Last name</Form.Label>
                      <Form.Control
                        type="text"
                        value={accountForm.lastName}
                        onChange={handleAccountFieldChange('lastName')}
                        autoComplete="family-name"
                        maxLength={120}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group controlId="settingsEmail">
                      <Form.Label>Email / Username</Form.Label>
                      <Form.Control type="text" value={profile.email || profile.username || ''} readOnly />
                    </Form.Group>
                  </Col>
                  <Col xs={12}>
                    <Form.Group controlId="settingsOrganizations">
                      <Form.Label>Organizations</Form.Label>
                      <div className="settings-readonly-box">
                        {profile.organizations.length ? (
                          profile.organizations.map((organization) => (
                            <div className="settings-readonly-item" key={organization.id || organization.slug || organization.name}>
                              <strong>{organization.name || organization.slug || organization.id}</strong>
                              {organization.status && organization.status !== 'enabled' ? (
                                <span className="text-muted"> ({organization.status})</span>
                              ) : null}
                            </div>
                          ))
                        ) : (
                          <div className="text-muted">No organizations assigned to this account.</div>
                        )}
                      </div>
                    </Form.Group>
                  </Col>
                </Row>

                <div className="settings-actions">
                  <Button type="submit" disabled={accountSubmitting}>
                    {accountSubmitting ? 'Saving...' : 'Save changes'}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Card.Body>
      </Card>
    );
  }

  function renderSecuritySection() {
    const currentTwoFactor = security.twoFactor;
    const methodChanged = twoFactorMethodSelection !== currentTwoFactor.method;
    let twoFactorActionLabel = 'Update two-factor authentication';
    if (!methodChanged) {
      twoFactorActionLabel = 'Current method selected';
    } else if (currentTwoFactor.method === TWO_FACTOR_METHODS.DISABLED) {
      twoFactorActionLabel = 'Enable two-factor authentication';
    } else if (twoFactorMethodSelection === TWO_FACTOR_METHODS.DISABLED) {
      twoFactorActionLabel = 'Disable two-factor authentication';
    } else {
      twoFactorActionLabel = 'Change two-factor authentication method';
    }

    return (
      <Card className="settings-module-card">
        <Card.Body>
          <div className="settings-module-header">
            <h1>Security</h1>
            <p>Protect your account and keep your credentials current.</p>
          </div>

          <Card className="settings-subsection-card">
            <Card.Body>
              <div className="settings-subsection-header">
                <h2>Password</h2>
                <p>Change your Password if needed.</p>
              </div>

              {securityError ? <Alert variant="danger">{securityError}</Alert> : null}
              {securitySuccess ? <Alert variant="success">{securitySuccess}</Alert> : null}

              <Form onSubmit={handlePasswordSubmit} noValidate>
                <Row className="g-3">
                  <Col xs={12}>
                    <Form.Group controlId="settingsCurrentPassword">
                      <Form.Label>Current password</Form.Label>
                      <Form.Control
                        type="password"
                        value={securityForm.currentPassword}
                        onChange={handleSecurityFieldChange('currentPassword')}
                        autoComplete="current-password"
                      />
                    </Form.Group>
                  </Col>
                  <Col xs={12}>
                    <Form.Group controlId="settingsNewPassword">
                      <Form.Label>New password</Form.Label>
                      <Form.Control
                        type="password"
                        value={securityForm.newPassword}
                        onChange={handleSecurityFieldChange('newPassword')}
                        autoComplete="new-password"
                      />
                    </Form.Group>
                    <div className="mt-2">
                      <ProgressBar now={passwordStrength.percent} variant={passwordStrength.variant} />
                      <small className="d-block mt-1">{passwordStrength.label}</small>
                      <ul className="password-requirements mt-2 mb-0">
                        {passwordStrength.statuses.map((requirement) => (
                          <li key={requirement.id} className={requirement.met ? 'text-success' : 'text-muted'}>
                            {requirement.met ? '[x]' : '[ ]'} {requirement.label}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </Col>
                  <Col xs={12}>
                    <Form.Group controlId="settingsConfirmPassword">
                      <Form.Label>Confirm new password</Form.Label>
                      <Form.Control
                        type="password"
                        value={securityForm.confirmPassword}
                        onChange={handleSecurityFieldChange('confirmPassword')}
                        autoComplete="new-password"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <div className="settings-actions">
                  <Button type="submit" disabled={securitySubmitting}>
                    {securitySubmitting ? 'Updating...' : 'Update password'}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>

          <Card className="settings-subsection-card">
            <Card.Body>
              <div className="settings-subsection-header">
                <h2>Two-Factor Authentication</h2>
                <p>Add a second verification step to protect your account during sign-in and security changes.</p>
              </div>

              {twoFactorError ? <Alert variant="danger">{twoFactorError}</Alert> : null}
              {twoFactorSuccess ? <Alert variant="success">{twoFactorSuccess}</Alert> : null}

              <div className="settings-two-factor-status">
                {currentTwoFactor.enabled ? (
                  <Alert variant="secondary" className="mb-3">
                    Enabled: {getTwoFactorMethodLabel(currentTwoFactor.method)}. Backup recovery codes remaining: {currentTwoFactor.recoveryCodesRemaining}.
                  </Alert>
                ) : (
                  <div className="text-muted mb-3">Two-factor authentication is currently disabled.</div>
                )}
              </div>

              <Form.Group controlId="settingsTwoFactorMethod">
                <Form.Label>Method</Form.Label>
                <Form.Select
                  value={twoFactorMethodSelection}
                  onChange={(event) => {
                    setTwoFactorMethodSelection(event.target.value);
                    setTwoFactorError('');
                    setTwoFactorSuccess('');
                  }}
                >
                  <option value={TWO_FACTOR_METHODS.DISABLED}>Disabled</option>
                  <option value={TWO_FACTOR_METHODS.EMAIL}>Email</option>
                  <option value={TWO_FACTOR_METHODS.AUTHENTICATOR}>Authenticator app</option>
                </Form.Select>
                <Form.Text className="text-muted">
                  Email sends a verification code to your inbox. Authenticator app uses a one-time code from your mobile app.
                </Form.Text>
              </Form.Group>

              <div className="settings-actions">
                <Button
                  type="button"
                  disabled={!methodChanged}
                  onClick={openTwoFactorChangeFlow}
                >
                  {twoFactorActionLabel}
                </Button>
                {currentTwoFactor.enabled ? (
                  <Button
                    type="button"
                    variant="outline-secondary"
                    onClick={openRecoveryCodeRegenerationFlow}
                  >
                    Generate new backup recovery codes
                  </Button>
                ) : null}
              </div>
            </Card.Body>
          </Card>
        </Card.Body>
      </Card>
    );
  }

  function renderConnectionsSection() {
    return <PersonalConnections initialConnections={settings?.connections} initialUnavailable={settingsUnavailable} />;
  }

  function renderActiveSection() {
    switch (activeSection) {
      case 'security':
        return renderSecuritySection();
      case 'connections':
        return renderConnectionsSection();
      case 'account':
      default:
        return renderAccountSection();
    }
  }

  function getTwoFactorFlowTitle() {
    if (!twoFactorFlow.challengeToken) {
      return twoFactorFlow.purpose === 'change'
        ? 'Confirm your password'
        : 'Confirm your password';
    }

    if (twoFactorFlow.qrCodeDataUrl || twoFactorFlow.manualEntryKey) {
      return 'Set up your Authenticator app';
    }

    if (twoFactorFlow.useRecoveryCode) {
      return 'Enter a backup recovery code';
    }

    return twoFactorFlow.verificationMethod === TWO_FACTOR_METHODS.AUTHENTICATOR
      ? 'Enter your Authenticator app code'
      : 'Enter your email verification code';
  }

  function getTwoFactorFlowDescription() {
    if (!twoFactorFlow.challengeToken) {
      return twoFactorFlow.purpose === 'change'
        ? 'For security, re-enter your password to continue.'
        : 'For security, re-enter your password before generating new backup recovery codes.';
    }

    if (twoFactorFlow.qrCodeDataUrl || twoFactorFlow.manualEntryKey) {
      return 'Scan the QR code with your Authenticator app or enter the manual setup key, then enter the 6-digit code generated by the app.';
    }

    if (twoFactorFlow.useRecoveryCode) {
      return 'Enter one of your unused backup recovery codes. Each code can only be used once.';
    }

    if (twoFactorFlow.verificationMethod === TWO_FACTOR_METHODS.AUTHENTICATOR) {
      return 'Enter the 6-digit code currently shown in your Authenticator app.';
    }

    return twoFactorFlow.destinationPreview
      ? `Enter the 6-digit code sent to ${twoFactorFlow.destinationPreview}.`
      : 'Enter the 6-digit code sent to your email.';
  }

  return (
    <>
      <HeaderNavbar />
      <div className="settings-page">
        <Row className="gx-4 settings-layout">
          <Col lg={3} className="settings-sidebar-column">
            <Card className="settings-nav-card">
              <Card.Body>
                <div className="settings-nav-heading">Settings</div>
                <div className="settings-nav-subheading">{profile.email || profile.username || 'Account'}</div>
                <div className="settings-nav-list">
                  {SETTINGS_SECTIONS.map((section) => (
                    <button
                      key={section.id}
                      type="button"
                      className={`settings-nav-button ${activeSection === section.id ? 'active' : ''}`}
                      onClick={() => handleSectionChange(section.id)}
                    >
                      {section.label}
                    </button>
                  ))}
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col lg={9} className="settings-content-column">
            {loading ? (
              <Card className="settings-module-card">
                <Card.Body>
                  <div className="settings-loading-state">
                    <Spinner animation="border" />
                  </div>
                </Card.Body>
              </Card>
            ) : pageError ? (
              <Card className="settings-module-card">
                <Card.Body>
                  <Alert variant="danger" className="mb-3">{pageError}</Alert>
                  <Button onClick={loadSettings}>Try again</Button>
                </Card.Body>
              </Card>
            ) : (
              renderActiveSection()
            )}
          </Col>
        </Row>
      </div>

      <Modal show={twoFactorFlow.show} onHide={closeTwoFactorFlow} centered>
        <Modal.Header closeButton>
          <Modal.Title>{getTwoFactorFlowTitle()}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleTwoFactorFlowSubmit} noValidate>
          <Modal.Body>
            <p className="text-muted mb-3">{getTwoFactorFlowDescription()}</p>

            {twoFactorFlow.error ? <Alert variant="danger">{twoFactorFlow.error}</Alert> : null}

            {!twoFactorFlow.challengeToken ? (
              <Form.Group controlId="twoFactorFlowPassword">
                <Form.Label>Password</Form.Label>
                <Form.Control
                  type="password"
                  value={twoFactorFlow.password}
                  onChange={(event) => setTwoFactorFlow((prev) => ({
                    ...prev,
                    password: event.target.value,
                    error: ''
                  }))}
                  autoComplete="current-password"
                  autoFocus
                />
              </Form.Group>
            ) : (
              <>
                {(twoFactorFlow.qrCodeDataUrl || twoFactorFlow.manualEntryKey) ? (
                  <div className="settings-two-factor-setup">
                    {twoFactorFlow.qrCodeDataUrl ? (
                      <div className="settings-two-factor-qr-wrapper">
                        <img src={twoFactorFlow.qrCodeDataUrl} alt="Authenticator app QR code" className="settings-two-factor-qr" />
                      </div>
                    ) : null}
                    {twoFactorFlow.manualEntryKey ? (
                      <div className="settings-two-factor-manual-key">
                        <div className="settings-two-factor-manual-label">Manual setup key</div>
                        <code>{twoFactorFlow.manualEntryKey}</code>
                      </div>
                    ) : null}
                  </div>
                ) : null}

                {twoFactorFlow.allowRecoveryCode ? (
                  <Button
                    type="button"
                    variant="link"
                    className="settings-two-factor-toggle-button"
                    onClick={() => setTwoFactorFlow((prev) => ({
                      ...prev,
                      useRecoveryCode: !prev.useRecoveryCode,
                      code: '',
                      recoveryCode: '',
                      error: ''
                    }))}
                  >
                    {twoFactorFlow.useRecoveryCode ? 'Use the regular verification code instead' : 'Use a backup recovery code instead'}
                  </Button>
                ) : null}

                {twoFactorFlow.useRecoveryCode ? (
                  <Form.Group controlId="twoFactorFlowRecoveryCode">
                    <Form.Label>Backup recovery code</Form.Label>
                    <Form.Control
                      type="text"
                      value={twoFactorFlow.recoveryCode}
                      onChange={(event) => setTwoFactorFlow((prev) => ({
                        ...prev,
                        recoveryCode: event.target.value,
                        error: ''
                      }))}
                      autoComplete="off"
                      autoCapitalize="characters"
                      spellCheck={false}
                      autoFocus
                    />
                  </Form.Group>
                ) : (
                  <Form.Group controlId="twoFactorFlowCode">
                    <Form.Label>
                      {twoFactorFlow.verificationMethod === TWO_FACTOR_METHODS.AUTHENTICATOR ? 'Authenticator app code' : 'Verification code'}
                    </Form.Label>
                    <Form.Control
                      type="text"
                      value={twoFactorFlow.code}
                      onChange={(event) => setTwoFactorFlow((prev) => ({
                        ...prev,
                        code: event.target.value,
                        error: ''
                      }))}
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      autoFocus
                    />
                  </Form.Group>
                )}

                {twoFactorFlow.verificationMethod === TWO_FACTOR_METHODS.EMAIL && !twoFactorFlow.useRecoveryCode ? (
                  <div className="settings-two-factor-helper-row">
                    <small className="text-muted">
                      Didn&apos;t get the email? Check your spam folder and request a new code.
                    </small>
                    <Button
                      type="button"
                      variant="outline-secondary"
                      size="sm"
                      disabled={twoFactorFlow.loading}
                      onClick={handleTwoFactorChallengeResend}
                    >
                      Resend code
                    </Button>
                  </div>
                ) : null}
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" type="button" onClick={closeTwoFactorFlow} disabled={twoFactorFlow.loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={twoFactorFlow.loading}>
              {twoFactorFlow.loading ? 'Working...' : (twoFactorFlow.challengeToken ? 'Continue' : 'Continue')}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal
        show={recoveryCodesModal.show}
        onHide={() => setRecoveryCodesModal({ show: false, codes: [], title: '', message: '', copyState: 'idle' })}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{recoveryCodesModal.title || 'Backup recovery codes'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted">{recoveryCodesModal.message || 'Store these backup recovery codes somewhere safe. Each code can only be used once.'}</p>
          <div className="settings-recovery-codes-box">
            {(Array.isArray(recoveryCodesModal.codes) ? recoveryCodesModal.codes : []).map((code) => (
              <code key={code} className="settings-recovery-code-entry">{code}</code>
            ))}
          </div>
          {recoveryCodesModal.copyState === 'copied' ? (
            <Alert variant="success" className="mt-3 mb-0">Backup recovery codes copied.</Alert>
          ) : null}
          {recoveryCodesModal.copyState === 'failed' ? (
            <Alert variant="warning" className="mt-3 mb-0">Unable to copy automatically. Use the download button or copy them manually.</Alert>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" type="button" onClick={handleCopyRecoveryCodes}>
            Copy codes
          </Button>
          <Button variant="outline-secondary" type="button" onClick={handleDownloadRecoveryCodes}>
            Download codes
          </Button>
          <Button
            type="button"
            onClick={() => setRecoveryCodesModal({ show: false, codes: [], title: '', message: '', copyState: 'idle' })}
          >
            I&apos;ve saved these codes
          </Button>
        </Modal.Footer>
      </Modal>
      <Footer />
    </>
  );
}

export default Settings;
